import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {StudyPhase, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {NameTemplateType} from 'name-templates/NameTemplateConstants';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import AddExperimentModal from 'studies/sidepanel/AddExperimentModal';
import {
  getProcedureRunUIStringForStudyType,
  getProcedureTypeForStudyTypeAllowNull,
} from 'studies/TypedStudyUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import runModal from 'ui-library/runModal';

import {
  AddExperimentButtonQuery,
  AddExperimentButtonQueryVariables,
} from './__generated/AddExperimentButtonQuery';

type StudyNode = ExtractByTypename<AddExperimentButtonQuery['study'], 'StudyNode'>;
interface ButtonProps {
  disabled: boolean;
  tooltipText: string | null;
}

export const ADD_EXPERIMENT_BUTTON_QUERY = gql`
  query AddExperimentButtonQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        studySchema {
          id
          studyType
          nameTemplates {
            ... on NameTemplateNode {
              templateType
              nameTemplateParts {
                ... on NameTemplatePartNode {
                  type
                }
              }
            }
          }
        }
        phase
        procedureRunCount
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function isButtonDisabled(study: StudyNode): ButtonProps {
  const buttonStatus: ButtonProps = {disabled: false, tooltipText: null};

  const studyType = study.studySchema.studyType;
  const procedureType = getProcedureTypeForStudyTypeAllowNull(studyType);
  if (procedureType == null || studyType === StudyType.CUSTOM) {
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = `Procedure runs can not be added to this study`;
  } else if (!isFeatureEnabledForProcedureType(procedureType, ProceduresFeature.PLAN_EXPERIMENT)) {
    const procedureTypeName = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.SENTENCE,
      plural: true,
    });
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = `You do not have sufficient permissions to use ${procedureTypeName}. Please contact your administrator.`;
  } else if (study.phase !== StudyPhase.EXECUTION) {
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = `Reopen your study to add ${getProcedureRunUIStringForStudyType(studyType, {includeArticle: true})}`;
  } else if (
    (studyType === StudyType.ASSAY_DEVELOPMENT &&
      study.procedureRunCount >= BenchlingConfig.STUDIES_MAX_NUM_EXPERIMENTS_PER_ASSAY_DEV_STUDY) ||
    (studyType === StudyType.PROCESS_DEVELOPMENT &&
      study.procedureRunCount >= BenchlingConfig.STUDIES_MAX_NUM_RUNS_PER_PD_STUDY)
  ) {
    buttonStatus.disabled = true;
    buttonStatus.tooltipText = `Maximum number of ${getProcedureRunUIStringForStudyType(studyType, {plural: true})} reached`;
  }
  return buttonStatus;
}

/**
 * This component should only be rendered for study types that support multiple experiments.
 */
export default function AddExperimentButton({
  studyId,
  studyType,
}: {
  studyId: StrictString;
  studyType: StudyType;
}): JSX.Element {
  const result = useBenchlingQuery<AddExperimentButtonQuery, AddExperimentButtonQueryVariables>(
    ADD_EXPERIMENT_BUTTON_QUERY,
    {variables: {studyId}, renderCachedDataWhenRefetching: true}
  );

  let buttonProps: ButtonProps = {disabled: true, tooltipText: null};
  let onClickHandler: () => void = noop;

  if (result.type === QueryResultType.PENDING) {
    buttonProps.tooltipText = 'Loading...';
  } else if (result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.study)) {
    const study: StudyNode = result.data.study;
    buttonProps = isButtonDisabled(study);
    const procedureRunNameTemplates = study.studySchema.nameTemplates.filter(
      (template) => template.templateType === NameTemplateType.PROCEDURE_RUN
    );
    assert(
      procedureRunNameTemplates.length <= 1,
      'Expected at most one procedure run name template present in the study schema.'
    );
    const hasExperimentNameTemplate =
      procedureRunNameTemplates.length > 0
        ? procedureRunNameTemplates[0].nameTemplateParts.length > 0
        : false;

    onClickHandler = async () => {
      await runModal(AddExperimentModal, {
        studyType: study.studySchema.studyType,
        studyId: study.id,
        hasExperimentNameTemplate,
        refetch: result.refetch,
      });
    };
  }

  return (
    <Tooltip isDisabled={!buttonProps.disabled} text={buttonProps.tooltipText}>
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        size={ButtonSize.MEDIUM}
        fullWidth={false}
        icon="plus"
        text={`Add ${getProcedureRunUIStringForStudyType(studyType)}`}
        onClick={onClickHandler}
        disabled={buttonProps.disabled}
        dataTestAttrs={{component: 'AddExperimentModal', element: 'addExperimentButton'}}
      />
    </Tooltip>
  );
}
